<template>
  <div>
    <div class="flex box-border w-full max-w-full h-6 pe-10">
      <input
        type="range"
        :min="min"
        :max="mid"
        :step="step"
        :value="selectedMin"
        :style="minStyle"
        class="appearance-none m-0 h-full min-w-5 shrink-0 basis-[calc(50%+1.25rem)] bg-transparent rounded-none outline-none cursor-pointer [-webkit-tap-highlight-color:transparent]
        [&::-webkit-slider-runnable-track]:box-border [&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-s-full [&::-webkit-slider-runnable-track]:bg-gray-200 [&::-webkit-slider-runnable-track]:bg-no-repeat
        [&::-webkit-slider-runnable-track]:bg-[linear-gradient(to_right,var(--color-gray-200)_var(--dri-gradient-position),var(--color-secondary)_var(--dri-gradient-position),var(--color-secondary))]
        [&::-moz-range-track]:box-border [&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-s-full [&::-moz-range-track]:bg-gray-200 [&::-moz-range-track]:bg-no-repeat
        [&::-moz-range-track]:bg-[linear-gradient(to_right,var(--color-gray-200)_var(--dri-gradient-position),var(--color-secondary)_var(--dri-gradient-position),var(--color-secondary))]
        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:box-border [&::-webkit-slider-thumb]:size-5 [&::-webkit-slider-thumb]:mt-0.75 [&::-webkit-slider-thumb]:-translate-y-1/2 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-gray-300 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-sm
        [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:box-border [&::-moz-range-thumb]:size-5 [&::-moz-range-thumb]:max-w-[99.99%] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-gray-300 [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow-sm"
        data-ready="true"
        @input="onMinInput"
        @focus="midMethod = 'ceil'"
        @mousedown="midMethod = 'ceil'"
        @touchstart.passive="midMethod = 'ceil'"
      >
      <input
        type="range"
        :min="mid"
        :max="max"
        :step="step"
        :value="selectedMax"
        :style="maxStyle"
        class="appearance-none m-0 h-full min-w-5 shrink-0 basis-[calc(50%+1.25rem)] bg-transparent rounded-none outline-none cursor-pointer [-webkit-tap-highlight-color:transparent]
        [&::-webkit-slider-runnable-track]:box-border [&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-e-full [&::-webkit-slider-runnable-track]:bg-gray-200 [&::-webkit-slider-runnable-track]:bg-no-repeat
        [&::-webkit-slider-runnable-track]:bg-[linear-gradient(to_right,var(--color-secondary),var(--color-secondary)_var(--dri-gradient-position),var(--color-gray-200)_var(--dri-gradient-position))]
        [&::-moz-range-track]:box-border [&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-e-full [&::-moz-range-track]:bg-gray-200 [&::-moz-range-track]:bg-no-repeat
        [&::-moz-range-track]:bg-[linear-gradient(to_right,var(--color-secondary),var(--color-secondary)_var(--dri-gradient-position),var(--color-gray-200)_var(--dri-gradient-position))]
        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:box-border [&::-webkit-slider-thumb]:size-5 [&::-webkit-slider-thumb]:mt-0.75 [&::-webkit-slider-thumb]:-translate-y-1/2 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-gray-300 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-sm
        [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:box-border [&::-moz-range-thumb]:size-5 [&::-moz-range-thumb]:max-w-[99.99%] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-gray-300 [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow-sm"
        data-ready="true"
        @input="onMaxInput"
        @focus="midMethod = 'floor'"
        @mousedown="midMethod = 'floor'"
        @touchstart.passive="midMethod = 'floor'"
      >
    </div>
    <div
      v-if="hasLabels"
      class="flex items-center justify-between text-sm mt-1"
    >
      <span>{{ minLabel }}</span>
      <span>{{ maxLabel }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = withDefaults(defineProps<{
  min?: number
  max?: number
  step?: number
  precision?: number
  minLabel?: string
  maxLabel?: string
}>(), {
  min: 0,
  max: 100,
  step: 1,
  precision: 3,
})

const range = defineModel<[number, number]>('range', {
  default: () => [0, 100],
})

const selectedMin = computed(() => range.value[0])
const selectedMax = computed(() => range.value[1])

const midMethod = ref<'ceil' | 'floor'>('ceil')

const mid = computed(() => {
  const half = (selectedMax.value - selectedMin.value) / 2
  const value = selectedMin.value + Math[midMethod.value](half / props.step) * props.step

  return Number(value.toFixed(props.precision))
})

function gradientPosition(fill: number) {
  const safeFill = Number.isFinite(fill) ? fill : 0
  const percentage = (safeFill * 100).toFixed(props.precision)
  const thumbOffset = (0.5 - safeFill).toFixed(props.precision)

  return `calc(${percentage}% + (${thumbOffset} * 1.25rem))`
}

function flexBasis(widthPercent: string) {
  return `calc(${widthPercent}% + 1.25rem)`
}

const minStyle = computed(() => {
  const span = props.max - props.min
  if (!span) {
    return {}
  }

  const leftWidth = (((mid.value - props.min) / span) * 100).toFixed(props.precision)
  const minFill = (selectedMin.value - props.min) / (mid.value - props.min) || 0

  return {
    'flexBasis': flexBasis(leftWidth),
    '--dri-gradient-position': gradientPosition(minFill),
  }
})

const maxStyle = computed(() => {
  const span = props.max - props.min
  if (!span) {
    return {}
  }

  const rightWidth = (((props.max - mid.value) / span) * 100).toFixed(props.precision)
  const maxFill = (selectedMax.value - mid.value) / (props.max - mid.value) || 0

  return {
    'flexBasis': flexBasis(rightWidth),
    '--dri-gradient-position': gradientPosition(maxFill),
  }
})

function onMinInput(event: Event) {
  midMethod.value = 'ceil'
  range.value = [Number((event.target as HTMLInputElement).value), selectedMax.value]
}

function onMaxInput(event: Event) {
  midMethod.value = 'floor'
  range.value = [selectedMin.value, Number((event.target as HTMLInputElement).value)]
}

// Labels
const hasLabels = computed(() => props.minLabel || props.maxLabel)
</script>
